// @core/stores/authn/AuthnAuthStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { authnApi } from '@core/api';
import type {
  InvalidateTokensRequest,
  LoginRecord,
  RefreshTokensRequest,
  RequestPasswordResetRequest,
  ResendVerificationEmailRequest,
  ResetPasswordRequest,
  SendOneTimeCodeRequest,
  SendVerificationEmailRequest,
  SignInRequest,
  SignInResponse,
  SignInWithCodeRequest,
  SignUpRequest,
  SignUpResponse,
  UpdatePasswordRequest,
  VerifyEmailRequest,
  VerifyPasswordRequest,
} from '@core/types/authn';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AuthnAuthStore {
  // List state
  items: LoginRecord[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: LoginRecord | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, LoginRecord>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): LoginRecord | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: LoginRecord | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Invalidate all user tokens (logout from all devices)
   */
  async InvalidateAllTokens(data: InvalidateTokensRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.InvalidateAllTokens(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Invalidate current tokens (logout)
   */
  async InvalidateCurrentTokens(data: InvalidateTokensRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.InvalidateCurrentTokens(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List user login history
   */
  async ListLoginRecords(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await authnApi.ListLoginRecords(params);

      runInAction(() => {
        this.items = response.login_records?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Refresh access and refresh tokens
   */
  async RefreshTokens(data: RefreshTokensRequest): Promise<SignInResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.RefreshTokens(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Request password reset link
   */
  async RequestPasswordReset(data: RequestPasswordResetRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.RequestPasswordReset(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resend email verification link
   */
  async ResendVerificationEmail(data: ResendVerificationEmailRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ResendVerificationEmail(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  async ResetPassword(data: ResetPasswordRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ResetPassword(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send one-time login code
   */
  async SendOneTimeCode(data: SendOneTimeCodeRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.SendOneTimeCode(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send email verification link
   */
  async SendVerificationEmail(data: SendVerificationEmailRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.SendVerificationEmail(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Sign in with email/username and password
   */
  async SignIn(data: SignInRequest): Promise<SignInResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.SignIn(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Sign in with one-time code
   */
  async SignInWithCode(data: SignInWithCodeRequest): Promise<SignInResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.SignInWithCode(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register a new user
   */
  async SignUp(data: SignUpRequest): Promise<SignUpResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.SignUp(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update user password (requires current password)
   */
  async UpdatePassword(data: UpdatePasswordRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.UpdatePassword(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Verify email with token
   */
  async VerifyEmail(data: VerifyEmailRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.VerifyEmail(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Verify password for sensitive action
   */
  async VerifyPassword(data: VerifyPasswordRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.VerifyPassword(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const authnAuthStore = new AuthnAuthStore();
